<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <div class="setup-ct">
      <game-competition-details-table
        v-if="!isCreationMode || localIsCompleted"
        :model="model"
      />
      <rest-form
        v-else-if="model"
        ref="competition-teams-form"
        :model="model"
        :rules="rules"
        :disabled="isReadOnly"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        @on-error="isLoading = false"
      >
        <form-section
          :title="$t('Gamification_ManualBracket_Label')"
          :description="getManualBracketDescription"
          :is-required="true"
        >
          <rest-form-item
            :label="$t('Gamification_BracketBuilder_Label')"
            required
          >
            <el-switch
              v-model="model.manual_bracket"
              :active-value="true"
              :inactive-value="false"
              active-color="#1d87c9"
              @change="onManualBracketChange"
            />
          </rest-form-item>
        </form-section>
        <form-section
          :title="$t('Gamification_GameCompetitionTeamsForm_ChooseSport')"
          :description="
            $t('Gamification_GameCompetitionTeamsForm_ChooseSportDescription')
          "
          :is-required="true"
        >
          <rest-form-item
            prop="sport_id"
            :label="$t('Gamification_GameCompetitionTeamsForm_SportType')"
            class="default-width"
            required
          >
            <el-select
              v-model="model.sport_id"
              filterable
              clearable
              @change="handleSportChange"
            >
              <el-option
                v-for="sport in sports"
                :key="sport.id"
                :value="sport.id"
                :label="sport.name"
              />
            </el-select>
          </rest-form-item>
        </form-section>
        <form-section
          :title="$t('Gamification_GameCompetitionTeamsForm_ChooseCountry')"
          :description="
            $t('Gamification_GameCompetitionTeamsForm_ChooseCountryDescription')
          "
          v-loading="loadingSportLocations"
          is-required
        >
          <rest-form-item
            prop="country_id"
            :label="$t('Gamification_GameCompetitionTeamsForm_Location')"
            class="default-width"
            required
          >
            <el-select
              v-model="model.country_id"
              filterable
              clearable
              :disabled="isSportTypeEmpty"
              :loading="loadingSportLocations"
              @change="handleCountryChange"
            >
              <el-option
                v-for="country in sportCountries"
                :key="country.id"
                :value="country.id"
                :label="country.name"
              />
            </el-select>
          </rest-form-item>
        </form-section>
        <form-section
          :title="
            $t('Gamification_GameCompetitionTeamsForm_CompetitionSelection')
          "
          :description="
            $t(
              'Gamification_GameCompetitionTeamsForm_CompetitionSelectionDescription'
            )
          "
          :is-required="true"
          v-loading="loadingSportCompetitions"
        >
          <rest-form-item
            prop="competition_id"
            :label="$t('Gamification_GameCompetitionTeamsForm_Competition')"
            class="default-width"
            required
          >
            <el-select
              v-model="model.competition_id"
              filterable
              clearable
              :disabled="!model.country_id"
              :loading="loadingSportCompetitions"
            >
              <el-option
                v-for="competition in sportCompetitions"
                :key="competition.id"
                :value="competition.id"
                :label="competition.name"
              />
            </el-select>
          </rest-form-item>
        </form-section>
      </rest-form>
      <div v-if="model.competition_id" class="team-table-wrapper">
        <div class="team-table-header">
          <span>{{
            $t('Gamification_GameCompetitionTeamsForm_TeamDetails')
          }}</span>
          <!-- @todo Hiding this button because of issues with the gamify backend -->
          <el-button
            :disabled="isReadOnly"
            v-if="false"
            @click="downloadCompetitionTeams()"
            type="primary"
            class="csv-button"
            :loading="downloadLoading"
            >{{
              $t('Gamification_GameCompetitionTeamsForm_TeamsTable_DownloadCsv')
            }}</el-button
          >
        </div>
        <rest-list
          ref="teams-table"
          :payload="fetchCompetitionTeams"
          @dataLoaded="setTeamsData"
        >
          <rest-list-column
            :title="
              $t('Gamification_GameCompetitionTeamsForm_TeamsTable_TeamName')
            "
            prop="name"
            :min-width="100"
            :filterable="false"
            :sortable="false"
          />
        </rest-list>
      </div>
    </div>

    <div v-if="isCreationMode" class="footer">
      <div class="footer-ct">
        <el-button
          type="primary"
          :disabled="isSubmitDisabled"
          @click="saveCompetitionTeams"
          data-qa="next-step-btn"
        >
          {{ getNextStepLabel }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </div>
    </div>
  </rest-page>
</template>
<script>
import _ from 'lodash'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestForm from '@/common/components/rest/RestForm'
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormSection from '@/common/components/form/FormSection'
import GameCompetitionDetailsTable from '@/modules/gamification/components/games-campaigns/setup/bracketPredictor/GameCompetitionDetailsTable.vue'
import { checkIsButtonDisabled } from '@/modules/gamification/helpers'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import RestPage from '@/common/components/rest/RestPage.vue'
import RestApi from '@/core/services/RestApi'
import RestList from '@/common/components/rest/RestList.vue'
import RestListColumn from '@/common/components/rest/RestListColumn.vue'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'GameCompetitionTeamsForm',
  components: {
    RestListColumn,
    RestList,
    RestPage,
    RestForm,
    RestFormItem,
    FormSection,
    GameCompetitionDetailsTable
  },
  data() {
    return {
      requiredFields: ['sport_id', 'country_id', 'competition_id'],
      sports: [],
      countries: [],
      competitions: [],
      loadingSportLocations: false,
      loadingSportCompetitions: false,
      isLoading: false,
      step: GameCreationSubSteps.COMPETITION_TEAMS,
      model: {
        manual_bracket: undefined,
        sport_id: undefined,
        country_id: undefined,
        competition_id: undefined
      },
      initialStateModel: undefined,
      shouldGoToNextStep: false,
      downloadLoading: false,
      teams: []
    }
  },
  props: {
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    },
    isCompleted: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    isSubmitDisabled() {
      return (
        checkIsButtonDisabled(this.requiredFields, this.model) ||
        this.isLoading ||
        !this.hasTeams
      )
    },
    hasChanges() {
      return !_.isEqual(this.model, this.initialStateModel)
    },
    rules() {
      return {
        sport_id: {
          required: true,
          message: this.$t(
            'Gamification_GameCompetitionTeamsForm_SportTypeRequired'
          ),
          trigger: 'change'
        },
        country_id: {
          required: true,
          message: this.$t(
            'Gamification_GameCompetitionTeamsForm_CountryRequired'
          ),
          trigger: 'change'
        },
        competition_id: {
          required: true,
          message: this.$t(
            'Gamification_GameCompetitionTeamsForm_CompetitionRequired'
          ),
          trigger: 'change'
        }
      }
    },
    sportCountries() {
      if (!this.model.sport_id) {
        return []
      }
      return this.countries
    },
    sportCompetitions() {
      if (!this.model.sport_id) {
        return []
      }

      return this.competitions
    },
    isSportTypeEmpty() {
      return !this.model.sport_id
    },
    fetchEndpoint() {
      if (!this.isCreationMode) {
        return [this.fetchCompetitionConfigurationEndpoint()]
      }
      const endpoints = [this.fetchSportEndpoint()]
      if (this.localIsCompleted || this.$route.params.campaignId) {
        endpoints.push(this.fetchCompetitionConfigurationEndpoint())
      }
      return endpoints
    },
    fetchCompetitionTeams() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-sport-competition-teams/${this.model.competition_id}`,
        params: {
          manual_bracket: this.model.manual_bracket ? 1 : undefined
        }
      })
    },
    saveEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `gamify/${this.$route.params.id}/save-sport-competition/${this.gameCampaignId}`
      })
    },
    getNextStepLabel() {
      if (this.model.manual_bracket) {
        return this.localIsCompleted
          ? this.$t(
              'Gamification_GameCompetitionTeamsForm_Bracket_ContinueButton'
            )
          : this.$t('Gamification_GameCompetitionTeamsForm_Bracket_SaveButton')
      }

      return this.localIsCompleted
        ? this.$t('Gamification_GameCompetitionTeamsForm_ContinueButton')
        : this.$t('Gamification_GameCompetitionTeamsForm_SaveButton')
    },
    getManualBracketDescription() {
      return this.model.manual_bracket
        ? this.$t('Gamification_ManualBracket_OnDescription')
        : this.$t('Gamification_ManualBracket_OffDescription')
    },
    localIsCompleted() {
      return (
        !!(this.initialStateModel && this.initialStateModel.competition_id) ||
        this.isCompleted
      )
    },
    hasTeams() {
      if (this.localIsCompleted || !this.model.manual_bracket) {
        return true
      }

      return !!this.teams.length
    }
  },
  mounted() {
    if (!this.localIsCompleted) {
      this.initialStateModel = _.cloneDeep(this.model)
    }
  },
  watch: {
    isSubmitDisabled: {
      handler(newVal) {
        this.$emit('check', newVal)
      },
      immediate: true
    },
    'model.sport_id': function(newVal, oldVal) {
      if (oldVal !== newVal && newVal) {
        this.fetchSportLocationsEndpoint()
      }
    },
    'model.country_id': function(newVal, oldVal) {
      if (oldVal !== newVal && newVal) {
        this.fetchSportCompetitionsEndpoint()
      }
    },
    hasChanges: {
      handler(newVal) {
        this.$emit('checkChanges', newVal)
      },
      immediate: true
    },
    saveFlag: {
      async handler() {
        await this.saveCompetitionTeams(false)
      }
    }
  },
  methods: {
    saveCompetitionTeams(shouldGoToNextStep = true) {
      this.isLoading = true
      if (!this.localIsCompleted) {
        this.shouldGoToNextStep = shouldGoToNextStep
        this.$refs['competition-teams-form'].save()
      } else {
        if (shouldGoToNextStep) {
          this.$emit('goToNextStep', this.step)
        } else if (this.isCreationMode) {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        }
      }
    },
    afterSave(data, finishLoading) {
      finishLoading()
      this.isLoading = false
      this.initialStateModel = _.cloneDeep(this.model)

      if (this.shouldGoToNextStep) {
        this.$emit('goToNextStep', this.step)
      } else if (this.isCreationMode) {
        EventBus.$emit('updateStep', this.step)
        this.$nextTick(() => {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        })
      }
    },
    fetchSportEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-sports`,
        handler: data => {
          this.sports = data['data']
        }
      })
    },
    fetchCompetitionConfigurationEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-sport-competition-configuration/${this.gameCampaignId}`,
        handler: data => {
          this.model = data['data']
          if (this.model.manual_bracket === null) {
            this.model.manual_bracket = false
          }
          this.initialStateModel = _.cloneDeep(this.model)
        }
      })
    },
    fetchSportLocationsEndpoint() {
      if (!this.model.sport_id) {
        return
      }
      this.loadingSportLocations = true
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `gamify/${this.$route.params.id}/campaign/sport/${this.model.sport_id}/countries`,
          params: {
            in_bracket: this.model.manual_bracket ? undefined : 1
          }
        })
      )
        .then(({ data }) => {
          this.countries = data['data']
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.loadingSportLocations = false
        })
    },
    fetchSportCompetitionsEndpoint() {
      this.loadingSportCompetitions = true
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `gamify/${this.$route.params.id}/get-sport-competitions`,
          params: {
            sport_id: this.model.sport_id,
            country_id: this.model.country_id,
            has_bracket: this.model.manual_bracket ? undefined : 1
          }
        })
      )
        .then(({ data }) => {
          this.competitions = data['data']
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.loadingSportCompetitions = false
        })
    },
    handleSportChange() {
      if (this.model.country_id) {
        this.model.country_id = null
      }

      if (this.model.competition_id) {
        this.model.competition_id = null
      }
    },
    handleCountryChange() {
      if (this.model.competition_id) {
        this.model.competition_id = null
      }
    },
    downloadCompetitionTeams() {
      this.downloadLoading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/competition/teams/download`
        })
      )
        .then(({ data }) => {
          this.$message({
            message: data.message,
            type: 'success'
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.downloadLoading = false
        })
    },
    onManualBracketChange(payload) {
      EventBus.$emit('updateCampaign', { manual_bracket: payload })
      if (!this.localIsCompleted) {
        if (this.model.country_id) {
          this.model.country_id = undefined
          this.model.competition_id = undefined
        }
        this.fetchSportLocationsEndpoint()
      }
    },
    setTeamsData(payload) {
      this.teams = payload
    }
  },
  beforeDestroy() {
    if (!this.localIsCompleted) {
      EventBus.$emit('updateCampaign', { manual_bracket: false })
    }
  }
}
</script>
<style lang="scss" scoped>
.setup-ct {
  padding-bottom: 60px;
}
.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}
.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
.default-width {
  width: 596px;
}
.team-table-wrapper {
  display: flex;
  flex-direction: column;
  gap: 11px;

  .team-table-header {
    display: flex;
    justify-content: space-between;
    margin-top: 34px;
  }

  span {
    font-size: 16px;
    font-weight: 600;
    align-self: end;
  }
}
.csv-button {
  margin-left: auto;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/setup/bracketPredictor/GameCompetitionTeamsForm.vue